import catalog from './localizacao.json'
import ptBR from '../locales/pt-BR.json'
import es from '../locales/es.json'
import en from '../locales/en.json'

/** Ficheiros de tradução por locale vue-i18n (alinhado ao catálogo). */
const LOCALE_MESSAGE_FILES = {
  'pt-BR': ptBR,
  'es': es,
  'en': en,
}

/** @typedef {{ locale: string, localizacaoDb: string, short: string, labelKey: string, flag: string }} LocalizacaoItem */

export const DEFAULT_LOCALE = catalog.defaultLocale

/** @type {LocalizacaoItem[]} */
export const LOCALIZACOES = catalog.items

const defaultItem = LOCALIZACOES.find((i) => i.locale === DEFAULT_LOCALE) ?? LOCALIZACOES[0]

export const DEFAULT_LOCALIZACAO_DB = defaultItem.localizacaoDb

/** @type {Record<string, string>} Idiomas.Localizacao → vue-i18n */
export const DB_TO_UI = Object.fromEntries(
  LOCALIZACOES.map((item) => [item.localizacaoDb, item.locale]),
)

/** @type {Record<string, string>} vue-i18n / app-locale → Idiomas.Localizacao */
export const UI_TO_DB = LOCALIZACOES.reduce((acc, item) => {
  acc[item.locale] = item.localizacaoDb
  if (item.locale === 'pt-BR') {
    acc.pt_BR = item.localizacaoDb
    acc.pt_br = item.localizacaoDb
  }
  return acc
}, {})

/**
 * Idiomas.Localizacao / JWT → vue-i18n (pt_br → pt-BR).
 * @param {string | null | undefined} localizacaoDb
 */
export function toUiLocale(localizacaoDb) {
  if (!localizacaoDb)
    return DEFAULT_LOCALE
  if (DB_TO_UI[localizacaoDb])
    return DB_TO_UI[localizacaoDb]
  const key = String(localizacaoDb).toLowerCase().replace('-', '_')
  if (key === 'pt_br' || key.startsWith('pt'))
    return DEFAULT_LOCALE
  const esItem = LOCALIZACOES.find((i) => i.locale === 'es')
  const enItem = LOCALIZACOES.find((i) => i.locale === 'en')
  if (key.startsWith('es') && esItem)
    return esItem.locale
  if (key.startsWith('en') && enItem)
    return enItem.locale
  return DEFAULT_LOCALE
}

/**
 * vue-i18n / app-locale → Idiomas.Localizacao (pt-BR → pt_br).
 * @param {string | null | undefined} localeUi
 */
export function toDbLocalizacao(localeUi) {
  if (!localeUi)
    return DEFAULT_LOCALIZACAO_DB
  if (UI_TO_DB[localeUi])
    return UI_TO_DB[localeUi]
  const key = String(localeUi).toLowerCase().replace('-', '_')
  if (key === 'pt_br' || key.startsWith('pt'))
    return DEFAULT_LOCALIZACAO_DB
  const esItem = LOCALIZACOES.find((i) => i.locale === 'es')
  const enItem = LOCALIZACOES.find((i) => i.locale === 'en')
  if (key.startsWith('es') && esItem)
    return esItem.localizacaoDb
  if (key.startsWith('en') && enItem)
    return enItem.localizacaoDb
  return DEFAULT_LOCALIZACAO_DB
}

/** @param {string} locale */
export function isSupportedUiLocale(locale) {
  return LOCALIZACOES.some((i) => i.locale === locale)
}

/** Idioma do navegador limitado ao catálogo suportado. */
export function detectBrowserLocale() {
  const browserLang = navigator.language || navigator.userLanguage || ''
  const normalized = browserLang.toLowerCase()
  for (const item of LOCALIZACOES) {
    const prefix = item.locale.split('-')[0].toLowerCase()
    if (normalized.startsWith(prefix))
      return item.locale
  }
  return DEFAULT_LOCALE
}

/**
 * Opções para seletores de idioma (login, header).
 * @param {(key: string) => string} t — função i18n (ex.: this.$t)
 */
export function buildLocaleOptions(t) {
  return LOCALIZACOES.map((item) => ({
    code: item.locale,
    short: item.short,
    label: t(item.labelKey),
    localizacaoDb: item.localizacaoDb,
    flag: item.flag,
  }))
}

/**
 * Sigla exibida no header (PT, ES, EN).
 * @param {string} locale — chave vue-i18n (pt-BR, en, es)
 */
export function shortLabelForLocale(locale) {
  const item = LOCALIZACOES.find((i) => i.locale === locale)
  return item?.short ?? LOCALIZACOES[0].short
}

/** Mensagens vue-i18n / router meta a partir do catálogo. */
export function buildLocaleMessages() {
  return Object.fromEntries(
    LOCALIZACOES.map((item) => [item.locale, LOCALE_MESSAGE_FILES[item.locale]]),
  )
}

export default LOCALIZACOES
